/**
 * Bullet rewrite suggestion panel — appears inline below a bullet.
 * Shows XYZ and CAR format suggestions with LLM comments.
 */
import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Check, X, RefreshCw } from 'lucide-react';
import { capabilityApi, resumeApi } from '@/lib/api';
import { Button } from './ui/button';
import { showErrorToast } from '@/lib/toast';

interface BulletRewritePanelProps {
  capabilityId: string;
  currentBody: string;
  onAccept: (format: 'xyz' | 'car', suggestion: string) => void;
  onAdoptSkills?: (skills: string[]) => Promise<void>;
  onDismiss: () => void;
  /** When provided, uses the resume-bullet rewrite endpoints instead of the capability endpoints */
  resumeId?: string;
  resumeBulletId?: string;
}

export function BulletRewritePanel({
  capabilityId,
  currentBody,
  onAccept,
  onAdoptSkills,
  onDismiss,
  resumeId,
  resumeBulletId,
}: BulletRewritePanelProps) {
  const queryClient = useQueryClient();
  const [acceptedFormat, setAcceptedFormat] = useState<'xyz' | 'car' | null>(null);
  const [xyzFeedback, setXyzFeedback] = useState('');
  const [carFeedback, setCarFeedback] = useState('');
  const [regeneratingFormat, setRegeneratingFormat] = useState<'xyz' | 'car' | null>(null);
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set());
  const [adoptingSkills, setAdoptingSkills] = useState(false);

  const isResumeBulletMode = !!(resumeId && resumeBulletId);

  const suggestions = useMutation({
    mutationFn: () =>
      isResumeBulletMode
        ? resumeApi.rewriteBullet(resumeId!, resumeBulletId!)
        : capabilityApi.getRewriteSuggestions(capabilityId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['credits', 'balance'] });
    },
    onError: (error: any) => {
      if (error?.statusCode === 402) {
        showErrorToast('Not enough credits. An admin can grant you more.');
      }
    },
  });

  const regenerate = useMutation({
    mutationFn: (params: { format: 'xyz' | 'car'; feedback: string }) =>
      isResumeBulletMode
        ? resumeApi.rewriteBulletRegenerate(resumeId!, resumeBulletId!, params.format, params.feedback)
        : capabilityApi.regenerateRewriteSuggestion(capabilityId, params.format, params.feedback),
    onSuccess: (data, params) => {
      void queryClient.invalidateQueries({ queryKey: ['credits', 'balance'] });
      // Update the suggestion for the regenerated format
      setSuggestions((prev) => {
        if (!prev) return prev;
        const formatKey = `${params.format}Format` as const;
        const newSuggestion = data[formatKey];
        if (newSuggestion) {
          return {
            ...prev,
            [formatKey]: newSuggestion,
          };
        }
        return prev;
      });
      setRegeneratingFormat(null);
    },
    onError: (error: any) => {
      if (error?.statusCode === 402) {
        showErrorToast('Not enough credits. An admin can grant you more.');
      }
    },
  });

  const [suggestionData, setSuggestions] = useState<{
    xyzFormat?: { suggestion: string; comment: string };
    carFormat?: { suggestion: string; comment: string };
    suggestedSkills?: string[];
  }>();

  // Load suggestions on mount
  useEffect(() => {
    suggestions.mutate();
  }, [capabilityId]);

  // Update local state when initial suggestions load
  useEffect(() => {
    if (suggestions.data && !suggestionData) {
      setSuggestions(suggestions.data);
    }
  }, [suggestions.data, suggestionData]);

  const handleAccept = (format: 'xyz' | 'car') => {
    const suggestion = suggestions.data?.[`${format}Format`]?.suggestion;
    if (suggestion) {
      setAcceptedFormat(format);
      onAccept(format, suggestion);
    }
  };

  if (suggestions.isPending) {
    return (
      <div className="bg-secondary/30 border-t border-b border-border px-4 py-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Sparkles className="h-4 w-4 animate-pulse" />
          Generating suggestions…
        </div>
      </div>
    );
  }

  if (suggestions.isError) {
    return (
      <div className="bg-destructive/10 border-t border-b border-destructive/20 px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-destructive">Failed to generate suggestions</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  const xyzData = suggestionData?.xyzFormat;
  const carData = suggestionData?.carFormat;
  const suggestedSkills = suggestionData?.suggestedSkills ?? [];

  const handleSkillToggle = (skill: string) => {
    const newSelected = new Set(selectedSkills);
    if (newSelected.has(skill)) {
      newSelected.delete(skill);
    } else {
      newSelected.add(skill);
    }
    setSelectedSkills(newSelected);
  };

  const handleAdoptSkills = async () => {
    if (selectedSkills.size === 0 || !onAdoptSkills) return;
    try {
      setAdoptingSkills(true);
      await onAdoptSkills(Array.from(selectedSkills));
      // Remove adopted skills from the suggestions and clear selection
      setSuggestions((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          suggestedSkills: (prev.suggestedSkills ?? []).filter((s) => !selectedSkills.has(s)),
        };
      });
      setSelectedSkills(new Set());
    } finally {
      setAdoptingSkills(false);
    }
  };

  return (
    <div className="bg-secondary/30 border-t border-b border-border px-4 py-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          <Sparkles className="h-3 w-3" />
          Rewrite suggestions
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="h-6 w-6 p-0"
          disabled={acceptedFormat !== null}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Suggested Skills — only shown in capability mode (not resume-bullet mode) */}
      {!isResumeBulletMode && suggestedSkills.length > 0 && onAdoptSkills && (
        <div className="bg-background border border-border rounded p-3 space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold text-foreground">Suggested Skills</h4>
            <Button
              size="sm"
              variant="outline"
              onClick={handleAdoptSkills}
              disabled={selectedSkills.size === 0 || adoptingSkills}
              className="shrink-0 h-8"
            >
              <Check className="h-3.5 w-3.5 mr-1" />
              {adoptingSkills ? 'Adopting…' : `Adopt`}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {suggestedSkills.map((skill) => (
              <button
                key={skill}
                onClick={() => handleSkillToggle(skill)}
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedSkills.has(skill)
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                }`}
              >
                {selectedSkills.has(skill) && <Check className="h-3 w-3 mr-1" />}
                {skill}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* XYZ Format */}
      {xyzData && (
        <div className="bg-background border border-border rounded p-3 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="text-xs font-semibold text-foreground mb-1">XYZ Format</h4>
              <p className="text-sm leading-relaxed text-foreground mb-2">
                {xyzData.suggestion}
              </p>
              <p className="text-sm text-muted-foreground italic">
                {xyzData.comment}
              </p>
            </div>
            {acceptedFormat !== 'xyz' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleAccept('xyz')}
                className="shrink-0 h-8"
              >
                <Check className="h-3.5 w-3.5 mr-1" />
                Accept
              </Button>
            )}
            {acceptedFormat === 'xyz' && (
              <div className="text-xs font-semibold text-green-600 flex items-center gap-1">
                <Check className="h-4 w-4" />
              </div>
            )}
          </div>
          {acceptedFormat !== 'xyz' && (
            <div className="flex gap-2 border-t border-border pt-2">
              <textarea
                placeholder="Feedback to regenerate..."
                value={xyzFeedback}
                onChange={(e) => setXyzFeedback(e.target.value)}
                className="flex-1 bg-transparent text-sm outline-none border border-input focus:border-primary rounded px-2 py-1.5 resize-none"
                rows={2}
                disabled={regenerate.isPending && regeneratingFormat === 'xyz'}
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setRegeneratingFormat('xyz');
                  regenerate.mutate({ format: 'xyz', feedback: xyzFeedback });
                }}
                disabled={regenerate.isPending || !xyzFeedback.trim()}
                className="shrink-0 h-10"
              >
                <RefreshCw className={`h-4 w-4 ${regenerate.isPending && regeneratingFormat === 'xyz' ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* CAR Format */}
      {carData && (
        <div className="bg-background border border-border rounded p-3 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="text-xs font-semibold text-foreground mb-1">CAR Format</h4>
              <p className="text-sm leading-relaxed text-foreground mb-2">
                {carData.suggestion}
              </p>
              <p className="text-sm text-muted-foreground italic">
                {carData.comment}
              </p>
            </div>
            {acceptedFormat !== 'car' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleAccept('car')}
                className="shrink-0 h-8"
              >
                <Check className="h-3.5 w-3.5 mr-1" />
                Accept
              </Button>
            )}
            {acceptedFormat === 'car' && (
              <div className="text-xs font-semibold text-green-600 flex items-center gap-1">
                <Check className="h-4 w-4" />
              </div>
            )}
          </div>
          {acceptedFormat !== 'car' && (
            <div className="flex gap-2 border-t border-border pt-2">
              <textarea
                placeholder="Feedback to regenerate..."
                value={carFeedback}
                onChange={(e) => setCarFeedback(e.target.value)}
                className="flex-1 bg-transparent text-sm outline-none border border-input focus:border-primary rounded px-2 py-1.5 resize-none"
                rows={2}
                disabled={regenerate.isPending && regeneratingFormat === 'car'}
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setRegeneratingFormat('car');
                  regenerate.mutate({ format: 'car', feedback: carFeedback });
                }}
                disabled={regenerate.isPending || !carFeedback.trim()}
                className="shrink-0 h-10"
              >
                <RefreshCw className={`h-4 w-4 ${regenerate.isPending && regeneratingFormat === 'car' ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
